import { Component, OnInit, Optional } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CreateCheckoutSessionRequest,
  CustomerSubscriptionsService,
  UpdateSubscriptionRequest,
} from 'ldt-billing-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { environment } from 'src/environments/environment';
import { Log } from 'src/app/logger.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { BillingService } from 'src/app/core/services/billing.service';
import { takeUntil, filter, map } from 'rxjs/operators';
import { BaseProductSubscriptionComponent } from '../base-product-subscription.component';
import { Observable } from 'rxjs';

interface TierOption {
  priceId: string;
  price: number;
  qty: number;
}

@Component({
    selector: 'app-billing-products-people',
    templateUrl: './people.component.html',
    styleUrls: ['./people.component.scss'],
    standalone: false
})
export class PeopleProductSubscriptionComponent
  extends BaseProductSubscriptionComponent
  implements OnInit
{
  sortedPrices: TierOption[] = [];
  currentPriceId: string | null = null;
  selectedPriceId: string | null = null;
  isLoadingPrices = true;
  isDialog = false;
  isOrgAdmin$: Observable<boolean>;

  protected get productId(): string {
    return environment.peopleSubscriptionProductId;
  }

  constructor(
    subscriptionsService: CustomerSubscriptionsService,
    authService: AuthService,
    notificationService: NotificationService,
    billingService: BillingService,
    @Optional() private dialogRef?: MatDialogRef<PeopleProductSubscriptionComponent>
  ) {
    super(subscriptionsService, authService, notificationService, billingService);
    this.isDialog = !!this.dialogRef;
  }

  ngOnInit(): void {
    this.isLoadingPrices = true;
    super.initializeBaseSubscriptionLogic();
    this.isOrgAdmin$ = this.authService.$isOrgAdmin;

    // Gather all the prices for the People product and build the tiers for UI selection
    this.billingService.products$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((products) => products !== null),
        map((products) => products?.find((p) => p.id === environment.peopleSubscriptionProductId))
      )
      .subscribe({
        next: (peopleProduct) => {
          this.sortedPrices = peopleProduct?.prices
            ?.filter((price) => price.price !== undefined)
            ?.map((price) => {
              return {
                priceId: price.priceId,
                price: price.price,
                qty: price.qty,
              };
            })
            .filter((option) => option !== null) as TierOption[];

          this.sortedPrices.sort((a, b) => a.price - b.price);
          this.isLoadingPrices = false;
        },
        error: (err) => {
          Log.e('PeopleComponent: Error loading pricing data from BillingService:', err);
          this.notificationService.error(
            'Error loading product pricing details. Please try again later or contact support.'
          );
          this.isLoadingPrices = false;
        },
      });
  }

  // Any time the subscription is loaded or changed, update the UI
  protected onSubscriptionLoaded(): void {
    if (this.currentSubscription && this.currentSubscription.product?.price?.id) {
      this.currentPriceId = this.currentSubscription.product.price.id;
      this.selectedPriceId = this.currentPriceId;
    } else {
      this.currentPriceId = null;
    }
  }

  // Have the service refresh subs until the subscription is updated
  private pollForPeopleSubscriptionUpdate(priceId: string): void {
    super.pollUntilConditionMet(
      () => this.currentSubscription?.product?.price?.id === priceId,
      'Subscription plan successfully updated!'
    );
  }

  allowChangeSubscription(): boolean {
    return (
      this.currentSubscription?.product?.price?.id !== this.selectedPriceId &&
      !this.isPollingForUpdate &&
      !this.isLoadingSubscriptions &&
      !this.isLoadingPrices
    );
  }

  selectTier(priceId: string): void {
    this.selectedPriceId = priceId;
  }

  // This is for the initial subscription
  subscribe(): void {
    if (!this.selectedPriceId) {
      this.notificationService.warning('Please select a tier to subscribe.');
      return;
    }

    const req: CreateCheckoutSessionRequest = {
      priceId: this.selectedPriceId,
    };

    this.subscriptionsService
      .createCheckoutSession(this.authService.getSelectedOrgIdValue, req)
      .subscribe({
        next: (session) => super.handleStripeCheckoutSession(session.sessionUrl),
        error: (err) => super.handleBillingError(err, 'creating your subscription'),
      });
  }

  // This is when changing the tier of the subscription
  updateSubscription(): void {
    if (!this.currentSubscription || !this.currentSubscription.id) {
      Log.e('PeopleComponent: Current subscription not found for update.');
      this.notificationService.error(
        'Oops. Something went wrong. Please try again later or contact support.'
      );
      return;
    }
    if (!this.selectedPriceId) {
      Log.e('PeopleComponent: No tier selected for update.');
      this.notificationService.error(
        'Oops. Something went wrong. Please try again later or contact support.'
      );
      return;
    }
    if (this.currentSubscription.product?.price?.id === this.selectedPriceId) {
      this.notificationService.info('This is already your current plan.');
      return;
    }

    this.isLoadingSubscriptions = true;
    const req: UpdateSubscriptionRequest = {
      priceId: this.selectedPriceId,
    };
    this.subscriptionsService
      .updateSubscription(this.authService.getSelectedOrgIdValue, this.currentSubscription.id, req)
      .subscribe({
        next: () => {
          // Don't just handle the sub from the response, rather start polling so the service has the latest
          if (this.isDialog) {
            this.dialogRef?.close({ success: true });
          }
          this.pollForPeopleSubscriptionUpdate(this.selectedPriceId!);
        },
        error: (err) => super.handleBillingError(err, 'updating your subscription'),
      });
  }

  openIntercomForSales(): void {
    if ((window as any).Intercom) {
      (window as any).Intercom(
        'showNewMessage',
        'I would like to inquire about the 50,000+ People Data tier.'
      );
    } else {
      this.notificationService.error(
        'Please contact us at sales@workforce.ai for more information.'
      );
      Log.e('PeopleComponent: Intercom not available.');
    }
  }
}
